import {
    ELECTRONICS_TYPE,
    type DeskElectronicObject,
    type DeskMetalObject,
} from './contracts/pawnObjectDesk';

export type VerificationItemKind = 'metal' | 'general';

export const toDisplayText = (value: unknown): string => {
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }

    return '';
};

export interface SessionVerificationRow {
    id: number;
    contractId: number;
    contractNumber: string;
    objectDescription: string | null;
    differenceRon: number;
    differenceEur: number;
    notes: string | null;
    verified: number;
    verifiedBy: string | null;
    verifiedAt: string | null;
    exchangeRate?: number | null;
}

export interface VerificationItem {
    kind: VerificationItemKind;
    source: 'current' | 'verified';
    description: string;
    unitOfMeasure: string;
    quantity: number;
    unitPrice: number;
    valueRon: number;
    valueEur: number;
    purity?: number;
    metalType?: string;
    stoneWeightPercent?: number;
    stoneWeightGrams?: number;
    itemDate?: string;
}

export type VerificationItemPayload = Omit<VerificationItem, 'source' | 'itemDate'>;

export interface SessionVerificationOverview {
    previousSession: {
        sessionId: number;
        sessionNumber: number;
        startTime: string | null;
        endTime: string | null;
    } | null;
    verifications: SessionVerificationRow[];
    reportNumber: number;
    checklist: Array<{ id: number; name: string; completed: boolean }>;
}

/** Exchange rate the desk converts with: the contract's own, or a neutral 1 when unknown. */
export const resolveDeskExchangeRate = (row: Pick<SessionVerificationRow, 'exchangeRate'>): number => {
    const rate = Number(row.exchangeRate);
    return Number.isFinite(rate) && rate > 0 ? rate : 1;
};

const toMetalItem = (obj: DeskMetalObject): VerificationItemPayload => ({
    kind: 'metal',
    description: obj.denumire.trim(),
    unitOfMeasure: obj.um,
    quantity: obj.cantitate,
    unitPrice: obj.pretUnitar,
    valueRon: obj.sumainLei,
    valueEur: obj.sumainEuro,
    purity: obj.karat,
    metalType: obj.tipMetal,
    stoneWeightPercent: obj.piatraPercent,
    stoneWeightGrams: obj.piatra,
});

const toGeneralItem = (obj: DeskElectronicObject): VerificationItemPayload => ({
    kind: 'general',
    // An unnamed row is saved under its type, as the legacy desk row is named
    // "Electronice"/"Altele" until the operator describes it.
    description: obj.denumire.trim() || obj.tip,
    unitOfMeasure: obj.um,
    quantity: obj.cantitate,
    unitPrice: obj.pretUnitar,
    valueRon: obj.sumainLei,
    valueEur: obj.sumainEuro,
    metalType: obj.tip === ELECTRONICS_TYPE ? 'EL' : 'AL',
});

/**
 * Turns the ticked desk rows into the verification snapshot, mirroring Delphi's
 * `btnInregistrareVerifClick`, which saves every `Checked=1` row of the desk table.
 * Rows without a quantity carry nothing to verify and are left out.
 */
export const buildVerificationItemsFromDesk = (
    metalObjects: readonly DeskMetalObject[],
    electronicObjects: readonly DeskElectronicObject[],
): VerificationItemPayload[] => [
    ...metalObjects.filter(obj => obj.enabled && obj.cantitate > 0).map(toMetalItem),
    ...electronicObjects.filter(obj => obj.enabled && obj.cantitate > 0).map(toGeneralItem),
];

export const sumVerificationItemsRon = (items: readonly VerificationItemPayload[]): number =>
    items.reduce((sum, item) => sum + item.valueRon, 0);
